import { DeterminateLoader } from '@modules/common/components/DeterminateLoader';
import { ZOOM_ROTATE_KEYFRAMES } from '@modules/common/constants';
import { getRandomNumberInRange } from '@modules/common/utils';
import {
  AutoAwesomeRounded,
  BarChartRounded,
  CheckCircleRounded,
  IntegrationInstructionsRounded,
  ManageSearchRounded,
  SummarizeRounded,
} from '@mui/icons-material';
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Typography,
  alpha,
} from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { useMeasure } from 'react-use';

const PROGRESS_STEPS = [
  {
    icon: <IntegrationInstructionsRounded fontSize="small" color="inherit" />,
    text: 'Analyzing filtered feedback...',
  },
  {
    icon: <BarChartRounded fontSize="small" color="inherit" />,
    text: 'Quantifying feedback to gauge impact...',
  },
  {
    icon: <SummarizeRounded fontSize="small" color="inherit" />,
    text: 'Creating a comprehensive report of findings...',
  },
  {
    icon: <ManageSearchRounded fontSize="small" color="inherit" />,
    text: 'Finding useful examples to cite...',
  },
  {
    icon: <AutoAwesomeRounded fontSize="small" color="inherit" />,
    text: 'Summarizing key insights...',
  },
];

interface WisdomProcessingStepsLoaderProps {
  containerSxProps?: SxProps<Theme>;
  isLoadingComplete: boolean;
  onProgressComplete: () => void;
  shouldRandomizeProgress?: boolean;
  steps?: { text: string; icon: ReactNode }[];
  shouldSkipMultilineSkeleton?: boolean;
  direction?: 'row' | 'column';
  duration?: number;
}

export function WisdomProcessingStepsLoader({
  containerSxProps,
  isLoadingComplete = false,
  onProgressComplete,
  shouldRandomizeProgress = false,
  steps = PROGRESS_STEPS,
  shouldSkipMultilineSkeleton = false,
  direction,
  duration = 40,
}: WisdomProcessingStepsLoaderProps) {
  const [containerRef, { width: containerWidth }] =
    useMeasure<HTMLDivElement>();

  const [progress, setProgress] = useState(0);
  const [progressStepIndex, setProgressStepTextIndex] = useState(0);
  const [updateInterval, setUpdateInterval] = useState(500);
  const [completeDelay, setCompleteDelay] = useState(1000);
  const [maxProgress, setMaxProgress] = useState(90);

  useEffect(() => {
    if (shouldRandomizeProgress) {
      setUpdateInterval(getRandomNumberInRange(500, 1500));
      setCompleteDelay(getRandomNumberInRange(1000, 1500));
      setMaxProgress(getRandomNumberInRange(90, 98));
    }
  }, [shouldRandomizeProgress]);

  const currentProgressStep = steps[progressStepIndex];

  const renderProgressIndicator = () => {
    return progress >= 100 ? (
      <CheckCircleRounded
        sx={{
          color: 'success.light',
        }}
      />
    ) : (
      <Typography
        variant="body2"
        color="text.primary"
        minWidth="40px"
        textAlign="right"
      >
        {progress.toFixed(0)}%
      </Typography>
    );
  };

  const onProgressUpdate = (updatedProgress: number) => {
    setProgress(updatedProgress);
    const updatedProgressStepIndex = Math.min(
      Math.floor((updatedProgress / 100) * steps.length),
      steps.length - 1,
    );
    setProgressStepTextIndex(updatedProgressStepIndex);
  };

  const renderSkeleton = (width: string) => {
    return (
      <Skeleton
        width={width}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
        }}
      />
    );
  };

  const isRowDirection = direction
    ? direction === 'row'
    : containerWidth < 750
    ? 'column'
    : 'row';

  const stepTitle = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      color="text.disabled"
      key={currentProgressStep.text}
    >
      <Stack
        sx={{
          opacity: 0,
          animation: `${ZOOM_ROTATE_KEYFRAMES} 0.5s ease-in-out forwards`,
        }}
      >
        {currentProgressStep.icon}
      </Stack>
      <Typography variant="body2" color="text.primary">
        {currentProgressStep.text}
      </Typography>
    </Stack>
  );

  const progressIndicator = renderProgressIndicator();

  const determinateLoader = (
    <DeterminateLoader
      variant="linear"
      isCompleted={isLoadingComplete}
      onComplete={onProgressComplete}
      duration={duration * 1000} // seconds
      updateInterval={updateInterval}
      onProgressUpdate={onProgressUpdate}
      linearProgressProps={{
        sx: {
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.1),
          height: 10,
          borderRadius: 5,
          '& .MuiLinearProgress-bar': {
            bgcolor: progress >= 100 ? 'success.light' : undefined,
          },
        },
      }}
      maxProgress={maxProgress}
      completeDelay={completeDelay}
    />
  );

  const renderProgress = () => {
    if (isRowDirection) {
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={3}
          sx={{
            width: '100%',
          }}
        >
          {stepTitle}
          <Stack direction="row" alignItems="center" spacing={1} width="50%">
            <Stack flex={1}>{determinateLoader}</Stack>
            {progressIndicator}
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack
        spacing={2}
        sx={{
          width: '100%',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {stepTitle}
          {progressIndicator}
        </Stack>
        {determinateLoader}
      </Stack>
    );
  };

  return (
    <Box
      width="100%"
      ref={containerRef}
      component={Paper}
      variant="outlined"
      sx={{
        px: 2,
        py: 2,
        borderRadius: 2,
        ...containerSxProps,
      }}
    >
      <Stack spacing={3}>
        {renderProgress()}
        {shouldSkipMultilineSkeleton ? null : (
          <Stack>
            {renderSkeleton('100%')}
            {renderSkeleton('100%')}
            {renderSkeleton('100%')}
            {renderSkeleton('50%')}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
